import React from 'react';
import { Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
	PROGRAM_SCORE_BAND_LABELS,
	PROGRAM_SCORE_ROLE_LABELS,
} from '../../interfaces/CamProgramming.interface';
import type {
	ProgramScoreBand,
	ProgramScoreConfig,
	ProgramScoreRole,
	ProgramScoreRoleBreakdown,
	ProgramScoreRow,
} from '../../interfaces/CamProgramming.interface';

const { Text } = Typography;

const SCORE_BAND_COLORS: Record<ProgramScoreBand, string> = {
	on_target: 'green',
	watch: 'gold',
	needs_work: 'orange',
	critical: 'red',
};

const SCORE_BAND_TEXT: Record<ProgramScoreBand, string> = {
	on_target: '#389e0d',
	watch: '#d48806',
	needs_work: '#d4380d',
	critical: '#cf1322',
};

const ROLE_ORDER: ProgramScoreRole[] = [
	'programmer',
	'first_reviewer',
	'secondary_reviewer',
];

/** Score cell for a role the person may not have held at all. */
const RoleScoreCell: React.FC<{ role: ProgramScoreRoleBreakdown }> = ({
	role,
}) => {
	if (role.score === null || role.opportunities === 0) {
		return <Text>—</Text>;
	}
	return (
		<Tooltip
			title={`${role.opportunities} ${
				role.opportunities === 1 ? 'program' : 'programs'
			} · ${role.demerits} demerit pts · ${role.escaped_issues} escaped${
				role.credits ? ` · ${role.credits} credit pts` : ''
			}${
				role.training_excused_issues
					? ` · ${role.training_excused_issues} excused`
					: ''
			}`}
		>
			<span>{role.score.toFixed(1)}</span>
		</Tooltip>
	);
};

const RoleBreakdown: React.FC<{ row: ProgramScoreRow }> = ({ row }) => (
	<Table<ProgramScoreRoleBreakdown>
		className="cam-entry-table"
		size="small"
		rowKey="role"
		pagination={false}
		dataSource={ROLE_ORDER.map((role) => row.roles[role])}
		columns={[
			{
				title: 'Role',
				dataIndex: 'label',
				render: (_: unknown, role) => PROGRAM_SCORE_ROLE_LABELS[role.role],
			},
			{
				title: 'Opportunities',
				dataIndex: 'opportunities',
				align: 'right',
			},
			{
				title: 'Issues charged',
				dataIndex: 'attributed_issues',
				align: 'right',
			},
			{
				title: 'Escaped past this gate',
				dataIndex: 'escaped_issues',
				align: 'right',
			},
			{
				title: 'Excused as training',
				dataIndex: 'training_excused_issues',
				align: 'right',
			},
			{
				title: 'Issues caught',
				dataIndex: 'credited_catches',
				align: 'right',
			},
			{
				title: 'Demerit points',
				dataIndex: 'demerits',
				align: 'right',
			},
			{
				title: 'Credit points',
				dataIndex: 'credits',
				align: 'right',
				render: (value: number) => (value ? value.toFixed(1) : '—'),
			},
			{
				title: 'Demerits / opportunity',
				dataIndex: 'raw_demerits_per_opportunity',
				align: 'right',
				render: (value: number, role) =>
					role.opportunities ? value.toFixed(2) : '—',
			},
			{
				title: 'Score',
				dataIndex: 'score',
				align: 'right',
				render: (_: unknown, role) => <RoleScoreCell role={role} />,
			},
		]}
	/>
);

export interface ScoresTableProps {
	rows: ProgramScoreRow[];
	config: ProgramScoreConfig;
	loading: boolean;
	/** Highlighted person, whose weekly trend is overlaid on the chart. */
	selectedUserId: string | null;
	onSelect: (userId: string | null) => void;
}

const ScoresTable: React.FC<ScoresTableProps> = ({
	rows,
	config,
	loading,
	selectedUserId,
	onSelect,
}) => {
	const columns: ColumnsType<ProgramScoreRow> = [
		{
			title: '#',
			dataIndex: 'rank',
			width: 52,
			align: 'center',
		},
		{
			title: 'Person',
			key: 'person',
			fixed: 'left',
			width: 240,
			sorter: (a, b) => a.full_name.localeCompare(b.full_name),
			render: (_, row) => (
				<div className="cam-grid-part__info">
					<Text ellipsis={{ tooltip: row.full_name }}>{row.full_name}</Text>
					{row.is_provisional && (
						<Tooltip
							title={`Fewer than ${config.provisional_program_threshold} opportunities in this window. The score is not adjusted for that — it is just too little work to read as a verdict, so treat it as an early signal.`}
						>
							<Text style={{ fontSize: 12 }}>
								Provisional <InfoCircleOutlined />
							</Text>
						</Tooltip>
					)}
				</div>
			),
		},
		{
			title: 'Score',
			dataIndex: 'score',
			width: 100,
			align: 'right',
			defaultSortOrder: 'descend',
			sorter: (a, b) => a.score - b.score,
			render: (score: number, row) => (
				<span
					style={{
						fontSize: 16,
						fontWeight: 600,
						color: SCORE_BAND_TEXT[row.band],
					}}
				>
					{score.toFixed(1)}
				</span>
			),
		},
		{
			title: 'Band',
			dataIndex: 'band',
			width: 120,
			filters: (
				Object.keys(PROGRAM_SCORE_BAND_LABELS) as ProgramScoreBand[]
			).map((band) => ({
				text: PROGRAM_SCORE_BAND_LABELS[band],
				value: band,
			})),
			onFilter: (value, row) => row.band === value,
			render: (band: ProgramScoreBand) => (
				<Tag color={SCORE_BAND_COLORS[band]}>
					{PROGRAM_SCORE_BAND_LABELS[band]}
				</Tag>
			),
		},
		{
			title: (
				<Tooltip title="Programs this person programmed or reviewed in the window, summed across roles. This is the denominator their score is divided by.">
					<span>Opportunities</span>
				</Tooltip>
			),
			dataIndex: 'opportunities',
			width: 130,
			align: 'right',
			sorter: (a, b) => a.opportunities - b.opportunities,
		},
		{
			title: (
				<Tooltip title="The severity weight of every issue charged to this person, summed across all roles.">
					<span>Demerit pts</span>
				</Tooltip>
			),
			dataIndex: 'demerits',
			width: 140,
			align: 'right',
			sorter: (a, b) => a.demerits - b.demerits,
			render: (value: number, row) => (
				<span>
					{value.toFixed(1)}
					{row.training_excused_issues > 0 && (
						<Tooltip
							title={`${row.training_excused_issues} ${
								row.training_excused_issues === 1 ? 'issue' : 'issues'
							} excused as a training gap and not charged here.`}
						>
							<Tag
								color="blue"
								bordered={false}
								style={{ marginLeft: 6, marginRight: 0 }}
							>
								−{row.training_excused_issues}
							</Tag>
						</Tooltip>
					)}
				</span>
			),
		},
		{
			title: (
				<Tooltip
					title={`Credit for issues this person caught, worth ${(
						config.catch_credit_fraction * 100
					).toFixed(
						0
					)}% of each issue's points. Credit is netted off the demerits above before scoring and stops at zero, so it can pay a record back up but never past a clean one.`}
				>
					<span>Credit pts</span>
				</Tooltip>
			),
			dataIndex: 'credits',
			width: 130,
			align: 'right',
			sorter: (a, b) => a.credits - b.credits,
			render: (value: number, row) =>
				value ? (
					<span>
						{value.toFixed(1)}
						<Text type="secondary" style={{ fontSize: 12 }}>
							{' '}
							({row.credited_catches})
						</Text>
					</span>
				) : (
					<Text type="secondary">—</Text>
				),
		},
		{
			title: PROGRAM_SCORE_ROLE_LABELS.programmer,
			key: 'programmer_score',
			width: 130,
			align: 'right',
			sorter: (a, b) =>
				(a.roles.programmer.score ?? -1) - (b.roles.programmer.score ?? -1),
			render: (_, row) => <RoleScoreCell role={row.roles.programmer} />,
		},
		{
			title: PROGRAM_SCORE_ROLE_LABELS.first_reviewer,
			key: 'first_reviewer_score',
			width: 130,
			align: 'right',
			sorter: (a, b) =>
				(a.roles.first_reviewer.score ?? -1) -
				(b.roles.first_reviewer.score ?? -1),
			render: (_, row) => <RoleScoreCell role={row.roles.first_reviewer} />,
		},
		{
			title: PROGRAM_SCORE_ROLE_LABELS.secondary_reviewer,
			key: 'secondary_reviewer_score',
			width: 150,
			align: 'right',
			sorter: (a, b) =>
				(a.roles.secondary_reviewer.score ?? -1) -
				(b.roles.secondary_reviewer.score ?? -1),
			render: (_, row) => (
				<RoleScoreCell role={row.roles.secondary_reviewer} />
			),
		},
		{
			title: (
				<Tooltip title="Issues this person reported, and how many of those they caught during review. Only the ones caught on a program they held a role on, and were not themselves charged for, earn the credit in the column above.">
					<span>Issues logged</span>
				</Tooltip>
			),
			key: 'issues_reported',
			width: 140,
			align: 'right',
			sorter: (a, b) => a.issues_reported - b.issues_reported,
			render: (_, row) => (
				<span>
					{row.issues_reported}
					{row.issues_caught_in_review > 0 && (
						<Text style={{ fontSize: 12 }}>
							{' '}
							({row.issues_caught_in_review} in review)
						</Text>
					)}
				</span>
			),
		},
	];

	return (
		<Table<ProgramScoreRow>
			className="cam-grid-table cam-scores-table"
			size="small"
			rowKey="user_id"
			loading={loading}
			columns={columns}
			dataSource={rows}
			pagination={false}
			scroll={{ x: 'max-content' }}
			expandable={{
				expandedRowRender: (row) => <RoleBreakdown row={row} />,
				rowExpandable: (row) => row.opportunities > 0,
			}}
			rowClassName={(row) =>
				row.user_id === selectedUserId ? 'cam-table-row--selected' : ''
			}
			onRow={(row) => ({
				onClick: () =>
					onSelect(row.user_id === selectedUserId ? null : row.user_id),
			})}
			locale={{
				emptyText:
					'No programs were completed in this window, so there is nothing to score yet.',
			}}
		/>
	);
};

export default ScoresTable;
